import { useEffect, useState } from 'react';

import { isHeadlessPrintRoute } from '../utils/printRoute';

// How often to ask the browser to re-check /sw.js while the app stays open. The
// browser only checks for a new service worker on navigation, so a long-lived
// surface that never navigates (an all-day-open PWA window) would otherwise never
// surface the update prompt. One hour balances freshness against needless network
// chatter; a visibilitychange check catches reopened PWAs sooner.
const UPDATE_POLL_INTERVAL_MS = 60 * 60 * 1000;

let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration> | null =
  null;
let updatePollingStarted = false;

const isCypressRuntime = () =>
  typeof window !== 'undefined' &&
  Boolean((window as Window & { Cypress?: unknown }).Cypress);

const registerServiceWorkerOnce = () => {
  if (serviceWorkerRegistrationPromise) {
    return serviceWorkerRegistrationPromise;
  }

  serviceWorkerRegistrationPromise = navigator.serviceWorker
    // updateViaCache:'none' -> the browser never serves /sw.js itself from HTTP
    // cache when checking for updates. Caddy already sends no-store for /sw.js, so
    // this is belt-and-suspenders: update detection keeps working even if that
    // header ever regresses.
    .register('/sw.js', { updateViaCache: 'none' })
    .then((registration) => {
      console.log('SW registered:', registration);
      return registration;
    })
    .catch((error) => {
      serviceWorkerRegistrationPromise = null;
      console.log('SW registration failed:', error);
      throw error;
    });

  return serviceWorkerRegistrationPromise;
};

// Periodically nudge the browser to look for a new /sw.js so long-lived, never-
// navigating surfaces still discover deploys (and show the prompt). Runs once for
// the app's lifetime, matching the register-once singleton above.
const startUpdatePolling = () => {
  if (updatePollingStarted) {
    return;
  }
  updatePollingStarted = true;

  const checkForUpdate = () => {
    registerServiceWorkerOnce()
      .then((registration) => registration.update())
      .catch(() => {
        // Transient/offline failures are fine; the next tick retries.
      });
  };

  setInterval(checkForUpdate, UPDATE_POLL_INTERVAL_MS);

  if (typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    });
  }
};

export interface ServiceWorkerUpdateState {
  // A newer app shell is available; refreshing is always the user's choice.
  needRefresh: boolean;
}

export const useServiceWorker = (): ServiceWorkerUpdateState => {
  const [needRefresh, setNeedRefresh] = useState(false);

  useEffect(() => {
    // Skip SW registration on the /print/ route: it is the standalone PDF surface,
    // loaded ONLY by the backend's headless-chrome PDF worker (real users never
    // navigate to it). That headless browser shares one long-lived, persistent
    // browser context across every render (chromedp NewRemoteAllocator + NewContext,
    // no incognito; the container is restart: unless-stopped). If the print route
    // ever registered a service worker there, workbox's navigateFallback could serve
    // a STALE cached app shell on a later render — and if that shell's /api calls
    // fail (e.g. mid-deploy, before the internal `http://caddy` origin is live),
    // PrintMoldOrderReport flips __PRINT_READY on its error state and the worker
    // captures a silently-WRONG PDF (HTTP 200), with no Go-renderer fallback. The
    // headless context only ever loads /print/ URLs, so skipping registration here
    // keeps it permanently SW-free and makes every render hit the network for the
    // current code + live data. (Pre-cutover the headless worker rendered a local
    // file:// HTML file and never touched the app origin, so no app-origin SW exists
    // in that context today to clear.)
    if (
      isCypressRuntime() ||
      isHeadlessPrintRoute() ||
      !('serviceWorker' in navigator)
    ) {
      return;
    }

    let cancelled = false;
    const listenerCleanups: Array<() => void> = [];

    const promptForWorker = async (worker: ServiceWorker | null | undefined) => {
      if (!worker || cancelled) {
        return;
      }
      // A refresh may already have loaded the latest HTML before its worker
      // finishes installing. Compare entry URLs to avoid asking to refresh again.
      const entries = (doc: Document) => Array.from(
        doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'),
        (script) => script.getAttribute('src'),
      ).sort().join('|');
      const current = entries(document);
      if (current) {
        try {
          const response = await fetch('/index.html', { cache: 'no-store' });
          if (response.ok) {
            const latest = entries(new DOMParser().parseFromString(await response.text(), 'text/html'));
            if (latest && latest === current) return;
          }
        } catch {
          // The installed worker is still evidence of an update while offline.
        }
      }
      if (!cancelled) setNeedRefresh(true);
    };

    const watchInstallingWorker = (worker: ServiceWorker | null) => {
      if (!worker) {
        return;
      }
      const onStateChange = () => {
        // 'installed' while a controller already exists => a NEW version finished
        // installing behind the current one. It activates silently. The first-ever
        // install has no controller and is intentionally NOT prompted -- there is
        // nothing to update from.
        if (
          worker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          promptForWorker(worker);
        }
      };
      worker.addEventListener('statechange', onStateChange);
      onStateChange();
      listenerCleanups.push(() => {
        if (typeof worker.removeEventListener === 'function') {
          worker.removeEventListener('statechange', onStateChange);
        }
      });
    };

    const trackRegistration = (registration: ServiceWorkerRegistration) => {
      if (typeof registration.addEventListener !== 'function') {
        return;
      }
      // An update may already be parked from a previous load or another tab.
      if (registration.waiting && navigator.serviceWorker.controller) {
        promptForWorker(registration.waiting);
      }
      // Or a worker may already be mid-install before this listener attaches
      // (a deploy landing exactly during page load) -- catch its statechange too.
      if (registration.installing) {
        watchInstallingWorker(registration.installing);
      }
      const onUpdateFound = () =>
        watchInstallingWorker(registration.installing);
      registration.addEventListener('updatefound', onUpdateFound);
      listenerCleanups.push(() => {
        if (typeof registration.removeEventListener === 'function') {
          registration.removeEventListener('updatefound', onUpdateFound);
        }
      });
    };

    const start = () => {
      registerServiceWorkerOnce()
        .then((registration) => {
          if (cancelled) {
            return;
          }
          trackRegistration(registration);
          startUpdatePolling();
        })
        .catch(() => {
          // Error is already logged in registerServiceWorkerOnce; keep this non-throwing.
        });
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', start);
      listenerCleanups.forEach((remove) => remove());
    };
  }, []);

  return { needRefresh };
};
